import { format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { ActivityPageData } from '../pages/activities/ActivityPageData';
import { ActivityPageVariant } from '../pages/activities/ActivityPageVariant';
import { TFormElement } from './types';

const FIELD_PATH_SEPARATOR = '.';

export const splitFieldPath = (path: string): string[] => path.split(FIELD_PATH_SEPARATOR);
export const joinFieldPath = (path: (string | number)[]): string => path.join(FIELD_PATH_SEPARATOR);
export const fieldPathStartsWith = (path: string[], prefix: string[]): boolean => {
    if (prefix.length > path.length) {
        return false;
    }
    for (let i = 0; i < prefix.length; i++) {
        if (path[i] !== prefix[i]) {
            return false;
        }
    }
    return true;
};
export const fieldPathsEqual = (path1: string[], path2: string[]): boolean =>
    path1.length === path2.length && fieldPathStartsWith(path1, path2);

const getCurrentPath = (paths: (string | undefined)[]) => paths.filter(Boolean).join(FIELD_PATH_SEPARATOR);

export const injectCurrentPath =
    (parentEl: TFormElement & { path?: string; disabled?: boolean; readOnly?: boolean }) =>
    (childEl: TFormElement & { path?: string }, index?: number) => {
        return {
            ...childEl,
            path: getCurrentPath([parentEl.path, ...(index !== undefined ? [index.toString()] : []), childEl.path]),
            disabled: parentEl.disabled || childEl.disabled,
            readOnly: parentEl.readOnly || childEl.readOnly,
        };
    };

export const isCreateVariant = (
    variant: ActivityPageVariant
): variant is ActivityPageVariant.CREATE | ActivityPageVariant.CREATE_TEMPLATE =>
    [ActivityPageVariant.CREATE, ActivityPageVariant.CREATE_TEMPLATE].includes(variant);

export const getReadActivityData = (pageData: ActivityPageData) => {
    if (isCreateVariant(pageData.variant)) {
        throw new Error('Cannot access activityData on CREATE variants');
    }
    return pageData as Exclude<
        ActivityPageData,
        { variant: ActivityPageVariant.CREATE | ActivityPageVariant.CREATE_TEMPLATE }
    >;
};

export const getReadChangeRequestActivityData = (pageData: ActivityPageData) => {
    if (pageData.variant !== ActivityPageVariant.READ_CHANGE_REQUEST) {
        throw new Error('Cannot access activityData on Read Change Request variant');
    }
    return pageData as Extract<ActivityPageData, { variant: ActivityPageVariant.READ_CHANGE_REQUEST }>;
};

export const defaultDateConverter = {
    toPicker: (value: string | number | Date) => (value ? new Date(value) : null),
    fromPicker: (date: any) => date,
};

export const createGenevaTimeConverter = () => {
    const TIMEZONE = 'Europe/Zurich';

    const toBackend = (date: number | Date) => {
        const localMidnight = format(date, "yyyy-MM-dd'T'00:00:00");
        return zonedTimeToUtc(localMidnight, TIMEZONE).toISOString().replace('Z', '+00:00');
    };

    const fromBackend = (str: string | number | Date) =>
        parseISO(formatInTimeZone(new Date(str), TIMEZONE, 'yyyy-MM-dd'));

    return {
        toPicker: (value: string | number | Date) => {
            if (!value) return null;
            if (value instanceof Date) return value;
            const date = fromBackend(value);
            return isValid(date) ? date : null;
        },
        fromPicker: (date: number | Date) => {
            if (!date) return null;
            if (!isValid(date)) return date;
            return toBackend(date);
        },
    };
};

export const genevaDateConverter = createGenevaTimeConverter();
